import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Trash2, Download, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { newsletterAPI } from '../../utils/api';

export const NewsletterManager = () => {
  const { toast } = useToast();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSubscribers = useCallback(async () => {
    try {
      const response = await newsletterAPI.getSubscribers();
      setSubscribers(response.data);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      toast({
        title: "Error",
        description: "Failed to load newsletter subscribers.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to remove this subscriber?')) {
      try {
        await newsletterAPI.deleteSubscriber(id);
        toast({
          title: "Subscriber Removed",
          description: "Subscriber has been removed from the list.",
        });
        fetchSubscribers();
      } catch (error) {
        console.error('Error deleting subscriber:', error);
        toast({
          title: "Error",
          description: "Failed to remove subscriber.",
          variant: "destructive"
        });
      }
    }
  };

  const handleExportCSV = () => {
    if (subscribers.length === 0) {
      toast({
        title: "No Data",
        description: "No subscribers to export.",
        variant: "destructive"
      });
      return;
    }

    const csvContent = [
      ['Email', 'Status', 'Subscribed Date'].join(','),
      ...subscribers.map(sub => [
        sub.email,
        sub.status,
        new Date(sub.subscribed_at).toLocaleDateString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `newsletter_subscribers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Export Complete",
      description: `Exported ${subscribers.length} subscribers to CSV.`,
    });
  };

  if (loading) {
    return <div className="text-center py-8">Loading subscribers...</div>;
  }

  const activeSubscribers = subscribers.filter(s => s.status === 'active');
  const unsubscribedCount = subscribers.filter(s => s.status === 'unsubscribed').length;

  return (
    <div className="space-y-6" data-testid="newsletter-manager">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-2xl font-bold">Newsletter Subscribers</h3>
          <div className="flex gap-4 text-slate-600">
            <p>Active: <span className="font-semibold text-emerald-600">{activeSubscribers.length}</span></p>
            {unsubscribedCount > 0 && (
              <p>Unsubscribed: <span className="font-semibold text-slate-500">{unsubscribedCount}</span></p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setLoading(true);
              fetchSubscribers();
            }}
            className="border-slate-300"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleExportCSV}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      <Card className="border-2">
        <CardContent className="pt-6">
          {subscribers.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No subscribers yet. Share your newsletter signup to grow your list!
            </div>
          ) : (
            <div className="space-y-4">
              {subscribers.map((sub) => (
                <div 
                  key={sub.id} 
                  className={`flex items-center justify-between p-4 rounded-lg ${
                    sub.status === 'active' ? 'bg-slate-50' : 'bg-slate-100 opacity-75'
                  }`}
                  data-testid={`subscriber-${sub.id}`}
                >
                  <div className="flex items-center gap-4">
                    <Mail className={`w-5 h-5 ${sub.status === 'active' ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <div>
                      <p className="font-medium">{sub.email}</p>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <span>Subscribed: {new Date(sub.subscribed_at).toLocaleDateString()}</span>
                        {sub.status !== 'active' && (
                          <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded text-xs">
                            {sub.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(sub.id)}
                    data-testid={`delete-subscriber-${sub.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Summary */}
      {subscribers.length > 0 && (
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="border-2">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-emerald-600">{subscribers.length}</p>
              <p className="text-sm text-slate-600">Total Subscribers</p>
            </CardContent>
          </Card>
          <Card className="border-2">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-emerald-600">{activeSubscribers.length}</p>
              <p className="text-sm text-slate-600">Active Subscribers</p>
            </CardContent>
          </Card>
          <Card className="border-2">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-slate-600">
                {subscribers.length > 0 ? Math.round((activeSubscribers.length / subscribers.length) * 100) : 0}%
              </p>
              <p className="text-sm text-slate-600">Active Rate</p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
